import React, { useEffect, useMemo, useState } from "react";
import { fetchBranding, cachedBranding, brandingLogoUrl, publicBaseUrl } from "@/lib/api";
import { ShoppingBag, MapPin, Minus, Plus, Copy, CheckCircle2, Package, Users, Search, Truck, MessageCircle, Music2, Trophy, FlaskConical, ShieldCheck } from "lucide-react";

function PayCountdown({ deadline }) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, new Date(deadline).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  if (left === null) return null;
  if (left <= 0) return <p data-testid="pay-countdown" className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">Waktu pembayaran habis — pesanan otomatis dibatalkan.</p>;
  const h = Math.floor(left / 3600000), m = Math.floor((left % 3600000) / 60000), s = Math.floor((left % 60000) / 1000);
  return (
    <p data-testid="pay-countdown" className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
      Sisa waktu pembayaran: <span className="font-mono text-sm">{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span> — otomatis batal jika lewat 24 jam.
    </p>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const STATUS_LABEL = { pending: "Menunggu Konfirmasi", diproses: "Sedang Diproses", selesai: "Selesai", batal: "Dibatalkan" };

export default function Storefront() {
  const [brand, setBrand] = useState(cachedBranding() || { app_name: "Toko Kami", has_logo: false });
  const [tab, setTab] = useState("shop");
  const [products, setProducts] = useState(null);
  const [location, setLocation] = useState("gudang");
  const [cart, setCart] = useState({});
  const [step, setStep] = useState("shop");
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", province: "", city: "", notes: "" });
  const [shipInfo, setShipInfo] = useState(null);
  const [quote, setQuote] = useState(null);
  const [payCfg, setPayCfg] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [refCode, setRefCode] = useState("");
  const [trackCode, setTrackCode] = useState("");
  const [trackResult, setTrackResult] = useState(null);
  const [affForm, setAffForm] = useState({ name: "", phone: "", username: "", password: "" });
  const [affResult, setAffResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchBranding().then(setBrand); }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("gf_ref", ref.toUpperCase());
    setRefCode((ref || localStorage.getItem("gf_ref") || "").toUpperCase());
    fetch(`${API}/store/products`).then((r) => r.json()).then(setProducts).catch(() => setProducts([]));
    fetch(`${API}/store/shipping-info`).then((r) => r.json()).then(setShipInfo).catch(() => setShipInfo(null));
    fetch(`${API}/store/payment-config`).then((r) => r.json()).then((cfg) => {
      setPayCfg(cfg);
      if (cfg.enabled && !document.querySelector("script[data-midtrans-snap]")) {
        const s = document.createElement("script");
        s.src = cfg.snap_url;
        s.setAttribute("data-client-key", cfg.client_key);
        s.setAttribute("data-midtrans-snap", "1");
        s.async = true;
        document.body.appendChild(s);
      }
    }).catch(() => {});
  }, []);

  const locations = useMemo(() => (products && products.length > 0 ? products[0].stock : []), [products]);
  const warehouseAreas = useMemo(() => {
    const seen = new Set();
    return locations.filter((l) => l.city && !seen.has(l.city.toLowerCase()) && seen.add(l.city.toLowerCase()))
      .map((l) => `${l.city} (${l.location_name})`);
  }, [locations]);
  const locGeo = useMemo(() => locations.find((l) => l.location === location) || {}, [locations, location]);
  const stockOf = (p) => (p.stock.find((s) => s.location === location) || { qty: 0 }).qty;
  const cartItems = useMemo(() => (products || []).filter((p) => (cart[p.id] || 0) > 0).map((p) => ({ ...p, qty: cart[p.id] })), [products, cart]);
  const total = cartItems.reduce((a, p) => a + p.price * p.qty, 0);

  useEffect(() => {
    if (step !== "checkout" || !form.province || !form.city.trim()) { setQuote(null); return; }
    const t = setTimeout(() => {
      fetch(`${API}/store/shipping-quote`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location, province: form.province, city: form.city, subtotal: total }),
      }).then((r) => r.json()).then(setQuote).catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(t);
  }, [step, location, form.province, form.city, total]);
  const setQty = (p, q) => {
    const max = stockOf(p);
    const v = Math.max(0, Math.min(max, q));
    setCart((c) => ({ ...c, [p.id]: v }));
  };

  const openSnap = async (code) => {
    if (!payCfg?.enabled) return;
    try {
      const r = await fetch(`${API}/store/orders/${code}/snap`, { method: "POST" });
      const d = await r.json();
      if (r.ok && d.token && window.snap) {
        window.snap.pay(d.token, { onSuccess: () => {}, onPending: () => {}, onError: () => {}, onClose: () => {} });
      }
    } catch {}
  };

  const placeOrder = async () => {
    setError("");
    if (!form.customer_name.trim() || !form.phone.trim() || !form.address.trim()) return setError("Nama, No. HP, dan alamat wajib diisi.");
    if (!form.province || !form.city.trim()) return setError("Provinsi dan kota wajib diisi.");
    if (quote && !quote.allowed) return setError(quote.message || "Lokasi Anda belum terjangkau.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/store/orders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, location, items: cartItems.map((p) => ({ product_id: p.id, qty: p.qty })), affiliate_code: refCode }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Gagal membuat pesanan");
      setOrderResult(d);
      setStep("success");
      setCart({});
      openSnap(d.code);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const track = async () => {
    setTrackResult(null); setError("");
    try {
      await fetch(`${API}/store/orders/${encodeURIComponent(trackCode.trim())}/sync-payment`).catch(() => {});
      const r = await fetch(`${API}/store/orders/${encodeURIComponent(trackCode.trim())}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Pesanan tidak ditemukan");
      setTrackResult(d);
    } catch (e) { setTrackResult({ error: e.message }); }
  };

  const registerAffiliate = async () => {
    setError("");
    setBusy(true);
    try {
      const r = await fetch(`${API}/affiliate/register`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(affForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Gagal mendaftar");
      setAffResult(d);
    } catch (e) { setAffResult({ error: e.message }); }
    finally { setBusy(false); }
  };

  const shareLink = (code) => `${publicBaseUrl()}/toko?ref=${code}`;

  return (
    <div className="min-h-screen bg-white pb-28">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img data-testid="storefront-logo" src="/logo-tukangsolar.png" alt="Tukang Solar" className="h-11 w-auto object-contain" />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-extrabold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>{brand.app_name}</h1>
            <p className="text-xs text-slate-500">Sistem Orderan Langsung · <span className="font-bold text-slate-700">tukangsolar.com</span></p>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-2 flex gap-1">
          {[["shop", "Belanja", ShoppingBag], ["lacak", "Lacak Pesanan", Search], ["affiliate", "Affiliate", Users]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`store-tab-${id}`} onClick={() => setTab(id)}
              className={`flex-1 h-10 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 ${tab === id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
              <Icon size={14} />{label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 space-y-4">
        {refCode && tab === "shop" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 text-xs text-emerald-800">
            Anda belanja lewat link affiliate <b>{refCode}</b> — terima kasih sudah mendukung mitra kami!
          </div>
        )}

        {tab === "shop" && step === "shop" && (
          <>
            <section data-testid="hero-section" className="relative overflow-hidden bg-black rounded-2xl p-5 sm:p-7 text-white space-y-4">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#FFCC00]/25 blur-3xl pointer-events-none" />
              <img data-testid="hero-logo" src="/logo-tukangsolar.png" alt="Tukang Solar" className="h-16 w-auto object-contain drop-shadow-lg" />
              <p className="text-[11px] font-extrabold tracking-[0.25em] text-[#FFCC00] uppercase">tukangsolar.com</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight" style={{fontFamily:'Manrope'}}>
                Sistem Orderan Langsung <span className="text-[#FFCC00]">tukangsolar.com</span>
              </h2>
              <p className="text-sm text-slate-300 leading-relaxed">
                Sistem ini akan membuat Anda mendapatkan <b className="text-[#FFCC00]">free ongkir</b> jika kota atau provinsi Anda memiliki gudang terdekat kami. Jika gudang kami belum hadir di kota Anda, Anda cukup ajukan ke <b className="text-white">DM TikTok kami</b> kota mana yang Anda inginkan ada gudang kami. Sistem ini dibuat agar pelanggan setia kami tidak perlu menunggu lama dan juga bisa mendapatkan ongkir gratis.
              </p>
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[[MapPin, "Pilih kota Anda saat order"], [Truck, "Dikirim dari gudang terdekat"], [CheckCircle2, "Dapatkan gratis ongkir"]].map(([Icon, label], i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-2.5 text-center space-y-1.5">
                    <Icon size={18} className="mx-auto text-[#FFCC00]" />
                    <p className="text-[10px] font-bold leading-tight text-slate-200">{label}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Bayangkan — produk kami bisa sampai ke konsumen di tiap kota, mempersingkat pengiriman dan mempermurah produk sampai ke konsumen.
              </p>
            </section>
            <button data-testid="affiliate-strip" onClick={() => setTab("affiliate")} className="w-full text-left bg-[#FFCC00] rounded-2xl p-4 text-black space-y-1 active:scale-[0.99] transition-transform">
              <p className="font-extrabold text-sm flex items-center gap-2"><Users size={16} /> Untuk Anda Affiliator</p>
              <p className="text-xs leading-relaxed">Tenang saja — kami memiliki fitur affiliate layaknya marketplace lain. Saat Anda promosi produk kami dan orang membeli dari link Anda, Anda akan mendapatkan komisi. <b className="underline">Daftar di sini →</b></p>
            </button>
            {shipInfo && (
              <div data-testid="free-shipping-banner" className="bg-white border-2 border-[#FFCC00] rounded-xl p-4 text-black space-y-1.5 shadow-sm">
                <p className="flex items-center gap-2 font-extrabold text-sm tracking-wide"><Truck size={18} className="text-black" /> GRATIS ONGKIR JNT</p>
                <p className="text-xs text-slate-700">Min. belanja <b>{fmt(shipInfo.free_same_city_min)}</b> satu kota / Jabodetabek (dari Gudang Pusat) · min. <b>{fmt(shipInfo.free_same_province_min)}</b> satu provinsi</p>
                <p className="text-[11px] text-slate-700 bg-[#FFCC00]/25 rounded-lg px-2.5 py-1.5 leading-relaxed">
                  Fasilitas gratis ongkir hanya berlaku jika gudang kami sudah tersedia di provinsi/kota Anda.
                  {warehouseAreas.length > 0 && <> Gudang tersedia di: <b>{warehouseAreas.join(", ")}</b>.</>}
                </p>
              </div>
            )}
            <div data-testid="video-section" className="rounded-2xl overflow-hidden border-2 border-black shadow-sm bg-black">
              <div className="aspect-video w-full">
                <iframe className="w-full h-full" src="https://www.youtube.com/embed/kPX1cB9-eRM?autoplay=1&rel=0" title="Video tukangsolar.com" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              <a data-testid="video-fallback-link" href="https://youtu.be/kPX1cB9-eRM" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold text-[#FFCC00] bg-black hover:bg-slate-900 transition-colors">Video tidak muncul? Tonton langsung di YouTube →</a>
            </div>
            <div data-testid="credibility-section" className="bg-black rounded-2xl p-4 text-white space-y-2.5">
              <div className="flex items-start gap-3">
                <Trophy size={22} className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
                <p className="text-sm font-extrabold leading-snug">Aditif <span className="text-[#FFCC00]">pertama di Indonesia</span> yang masuk ke balapan GT3 Internasional</p>
              </div>
              <div className="flex items-start gap-3">
                <FlaskConical size={22} className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed">Sudah diuji di <b className="text-white">laboratorium Lemigas</b> dan <b className="text-white">dynotest di Thailand</b>.</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 flex items-center gap-1"><MapPin size={12} /> Dikirim dari</p>
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                {locations.map((l) => (
                  <button key={l.location} data-testid={`store-loc-${l.location}`} onClick={() => setLocation(l.location)}
                    className={`h-9 px-3 rounded-lg text-xs font-bold whitespace-nowrap ${location === l.location ? "bg-black text-[#FFCC00]" : "bg-white border border-slate-300 text-slate-600"}`}>
                    {l.location_name}
                  </button>
                ))}
              </div>
            </div>
            {products === null && <p className="text-sm text-slate-500">Memuat produk...</p>}
            {products !== null && products.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">Belum ada produk yang dijual online.</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {(products || []).map((p) => {
                const stok = stockOf(p);
                return (
                  <div key={p.id} data-testid={`store-product-${p.id}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    {p.photo_id ? (
                      <img src={`${API}/store/photo/${p.photo_id}`} alt={p.name} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 bg-slate-100 flex items-center justify-center text-slate-300"><Package size={32} /></div>
                    )}
                    <div className="p-3 space-y-1.5">
                      <p className="text-sm font-bold text-slate-900 leading-tight">{p.name}</p>
                      {p.description && <p className="text-xs text-slate-500 line-clamp-2">{p.description}</p>}
                      <p className="text-base font-extrabold text-black" style={{fontFamily:'Manrope'}}>{fmt(p.price)}<span className="text-xs font-normal text-slate-400"> /{p.unit}</span></p>
                      <p className={`text-[11px] font-bold ${stok > 0 ? "text-emerald-600" : "text-red-500"}`}>{stok > 0 ? `Stok ${stok} di lokasi ini` : "Stok habis di lokasi ini"}</p>
                      {stok > 0 && (
                        <div className="flex items-center justify-between bg-slate-100 rounded-lg p-1">
                          <button data-testid={`store-minus-${p.id}`} onClick={() => setQty(p, (cart[p.id] || 0) - 1)} className="w-8 h-8 rounded-md bg-white text-slate-700 font-bold flex items-center justify-center"><Minus size={14} /></button>
                          <span data-testid={`store-qty-${p.id}`} className="text-sm font-bold">{cart[p.id] || 0}</span>
                          <button data-testid={`store-plus-${p.id}`} onClick={() => setQty(p, (cart[p.id] || 0) + 1)} className="w-8 h-8 rounded-md bg-white text-slate-700 font-bold flex items-center justify-center"><Plus size={14} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "shop" && step === "checkout" && (
          <div className="space-y-3">
            <h2 className="text-xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Checkout</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              {["customer_name", "phone", "address", "notes"].map((f) => (
                <label key={f} className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{{ customer_name: "Nama", phone: "No. HP", address: "Alamat Lengkap", notes: "Catatan (opsional)" }[f]}</span>
                  {f === "address" || f === "notes" ? (
                    <textarea data-testid={`checkout-${f}`} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} rows={f === "address" ? 3 : 2} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm" />
                  ) : (
                    <input data-testid={`checkout-${f}`} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
                  )}
                </label>
              ))}
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Provinsi</span>
                  <select data-testid="checkout-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm bg-white">
                    <option value="">Pilih provinsi</option>
                    {(shipInfo?.provinces || []).map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Kota/Kabupaten</span>
                  <input data-testid="checkout-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Contoh: Bekasi" className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
                </label>
              </div>
              {shipInfo && (
                <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  Gratis ongkir: min. {fmt(shipInfo.free_same_city_min)} satu kota / Jabodetabek (dari Gudang Pusat), min. {fmt(shipInfo.free_same_province_min)} satu provinsi. Di bawah itu ongkir flat {fmt(shipInfo.flat_rate)} via JNT.
                </p>
              )}
              <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} /> Dikirim dari: <b>{locGeo.location_name}{locGeo.city ? ` — ${locGeo.city}${locGeo.province ? `, ${locGeo.province}` : ""}` : ""}</b></p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-1.5">
              {cartItems.map((p) => (
                <div key={p.id} className="flex justify-between text-sm">
                  <span className="text-slate-700">{p.name} × {p.qty}</span>
                  <span className="font-semibold text-slate-900">{fmt(p.price * p.qty)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-slate-100 text-sm">
                <span className="text-slate-600">Subtotal</span><span className="font-semibold text-slate-900">{fmt(total)}</span>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-600">Ongkir (JNT)</span>
                {quote === null ? (
                  <span data-testid="shipping-pending" className="text-xs text-slate-400">Isi provinsi & kota</span>
                ) : quote.allowed ? (
                  quote.is_free ? (
                    <span data-testid="shipping-free-badge" className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-extrabold">GRATIS</span>
                  ) : (
                    <span data-testid="shipping-cost" className="font-semibold text-slate-900">{fmt(quote.shipping_cost)}</span>
                  )
                ) : (
                  <span data-testid="shipping-na" className="text-xs text-red-500 font-bold">Tidak tersedia</span>
                )}
              </div>
              {quote && !quote.allowed && (
                <p data-testid="shipping-blocked" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{quote.message}</p>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-100 font-extrabold text-slate-900">
                <span>Total</span><span data-testid="checkout-grand-total">{fmt(total + (quote && quote.allowed ? quote.shipping_cost : 0))}</span>
              </div>
            </div>
            {error && <p data-testid="checkout-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
            <button data-testid="place-order-btn" onClick={placeOrder} disabled={busy || (quote && !quote.allowed)} className="w-full h-12 bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-50 text-black font-extrabold rounded-xl active:scale-[0.98] transition-transform">
              {busy ? "Memproses..." : `Buat Pesanan & Bayar ${fmt(total + (quote && quote.allowed ? quote.shipping_cost : 0))}`}
            </button>
            <p className="text-[11px] text-slate-400 text-center">Pembayaran dummy sementara — payment gateway segera hadir.</p>
            <button onClick={() => setStep("shop")} className="w-full h-10 text-sm font-bold text-slate-500">← Kembali belanja</button>
          </div>
        )}

        {tab === "shop" && step === "success" && orderResult && (
          <div className="bg-white rounded-xl border border-emerald-300 p-6 text-center space-y-3">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h2 className="text-xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Pesanan Berhasil Dibuat!</h2>
            <p className="text-sm text-slate-600">Simpan kode pesanan Anda untuk melacak:</p>
            <p data-testid="order-success-code" className="text-3xl font-extrabold text-black tracking-widest" style={{fontFamily:'Manrope'}}>{orderResult.code}</p>
            <p className="text-sm text-slate-600">Total <b>{fmt(orderResult.total)}</b>{orderResult.free_shipping ? " (gratis ongkir)" : orderResult.shipping_cost ? ` (termasuk ongkir ${fmt(orderResult.shipping_cost)})` : ""} — pesanan menunggu konfirmasi toko.</p>
            {payCfg?.enabled && <p data-testid="payment-hint" className="text-xs text-slate-500">Selesaikan pembayaran lewat popup Midtrans yang muncul, atau nanti lewat menu <b>Lacak</b>.</p>}
            <p data-testid="payment-deadline-note" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Batas pembayaran <b>24 jam</b> — lewat itu pesanan otomatis dibatalkan.</p>
            <button onClick={() => { setStep("shop"); setTab("lacak"); setTrackCode(orderResult.code); }} className="h-11 px-6 rounded-xl bg-slate-900 text-white text-sm font-bold">Lacak Pesanan</button>
          </div>
        )}

        {tab === "lacak" && (
          <div className="space-y-3">
            <h2 className="text-xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Lacak Pesanan</h2>
            <div className="flex gap-2">
              <input data-testid="track-input" value={trackCode} onChange={(e) => setTrackCode(e.target.value.toUpperCase())} placeholder="Contoh: GF-ABC123" className="flex-1 h-12 px-4 rounded-xl border border-slate-300 text-sm font-mono font-bold tracking-widest" />
              <button data-testid="track-btn" onClick={track} className="h-12 px-5 rounded-xl bg-slate-900 text-white text-sm font-bold">Lacak</button>
            </div>
            {trackResult && (
              trackResult.error ? (
                <p data-testid="track-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{trackResult.error}</p>
              ) : (
                <div data-testid="track-result" className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-mono font-bold text-slate-900">{trackResult.code}</span>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      <span data-testid="track-status-chip" className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${{ pending: "bg-amber-100 text-amber-800", diproses: "bg-blue-100 text-blue-800", selesai: "bg-emerald-100 text-emerald-800", batal: "bg-red-100 text-red-700" }[trackResult.status]}`}>{STATUS_LABEL[trackResult.status]}</span>
                      {trackResult.payment_status && (
                        <span data-testid="track-payment-chip" className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold ${trackResult.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : trackResult.payment_status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {trackResult.payment_status === "paid" ? "LUNAS" : trackResult.payment_status === "failed" ? "GAGAL BAYAR" : "BELUM DIBAYAR"}
                        </span>
                      )}
                    </div>
                  </div>
                  {trackResult.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm text-slate-600"><span>{it.name} × {it.qty}</span><span>{fmt(it.subtotal)}</span></div>
                  ))}
                  {trackResult.shipping_cost !== undefined && (
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Ongkir (JNT)</span>
                      {trackResult.free_shipping ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-extrabold">GRATIS</span> : <span>{fmt(trackResult.shipping_cost)}</span>}
                    </div>
                  )}
                  <p className="text-sm font-bold text-slate-900 pt-2 border-t border-slate-100 flex justify-between"><span>Total</span><span>{fmt(trackResult.total)}</span></p>
                  <p className="text-xs text-slate-500">Dikirim dari {trackResult.location_name} · Tujuan {trackResult.city}{trackResult.province ? `, ${trackResult.province}` : ""}</p>
                  {trackResult.resi_no && (
                    <div data-testid="track-resi" className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 space-y-2">
                      <p className="text-xs text-slate-600">Paket dikirim via <b>{trackResult.courier_name || (trackResult.courier || "").toUpperCase()}</b></p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-extrabold text-slate-900 text-base">{trackResult.resi_no}</span>
                        <button data-testid="track-resi-copy" onClick={() => { navigator.clipboard.writeText(trackResult.resi_no); }} className="px-2.5 py-1 rounded-lg bg-white border border-blue-300 text-[10px] font-bold text-blue-700">Salin</button>
                      </div>
                      {trackResult.tracking_url && (
                        <a data-testid="track-resi-link" href={trackResult.tracking_url} target="_blank" rel="noreferrer" className="block text-center h-10 leading-10 bg-blue-600 hover:bg-blue-700 text-white text-xs font-extrabold rounded-lg active:scale-[0.98] transition-transform">Lacak Paket di {trackResult.courier_name || "Kurir"} →</a>
                      )}
                    </div>
                  )}
                  {trackResult.payment_status === "pending" && trackResult.status === "pending" && trackResult.payment_deadline && <PayCountdown deadline={trackResult.payment_deadline} />}
                  {payCfg?.enabled && trackResult.payment_status === "pending" && trackResult.status !== "batal" && (
                    <button data-testid="track-pay-btn" onClick={() => openSnap(trackResult.code)} className="w-full h-11 bg-[#FFCC00] hover:bg-[#e6b800] text-black font-extrabold rounded-xl active:scale-[0.98] transition-transform">Bayar Sekarang</button>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {tab === "affiliate" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Program Affiliate</h2>
              <p className="text-sm text-slate-600">Daftar gratis, bagikan link Anda, dan dapatkan komisi dari setiap pesanan yang masuk lewat link Anda.</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <h3 className="font-bold text-slate-900">Daftar Affiliate</h3>
              <input data-testid="affiliate-name" value={affForm.name} onChange={(e) => setAffForm({ ...affForm, name: e.target.value })} placeholder="Nama Anda" className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
              <input data-testid="affiliate-phone" value={affForm.phone} onChange={(e) => setAffForm({ ...affForm, phone: e.target.value })} placeholder="No. HP" className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="affiliate-username" value={affForm.username} onChange={(e) => setAffForm({ ...affForm, username: e.target.value })} placeholder="Username" className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
                <input data-testid="affiliate-password" type="password" value={affForm.password} onChange={(e) => setAffForm({ ...affForm, password: e.target.value })} placeholder="Password" className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
              </div>
              <button data-testid="affiliate-register-btn" onClick={registerAffiliate} disabled={busy} className="w-full h-11 bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-50 text-black font-extrabold rounded-xl">Daftar & Dapatkan Link</button>
              {affResult && (
                affResult.error ? (
                  <p data-testid="affiliate-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{affResult.error}</p>
                ) : (
                  <div data-testid="affiliate-success" className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                    <p className="text-sm text-emerald-800">Kode Anda: <b className="font-mono">{affResult.code}</b> · Username: <b>{affResult.username}</b></p>
                    <div className="flex gap-2">
                      <input readOnly value={shareLink(affResult.code)} className="flex-1 h-10 px-3 rounded-lg border border-emerald-300 bg-white text-xs font-mono" />
                      <button data-testid="affiliate-copy-btn" onClick={() => { navigator.clipboard.writeText(shareLink(affResult.code)); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="h-10 px-3 rounded-lg bg-emerald-600 text-white text-xs font-bold flex items-center gap-1">
                        {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copied ? "Tersalin" : "Salin"}
                      </button>
                    </div>
                    <p className="text-xs text-emerald-700">Simpan username & password Anda. Pantau komisi dengan masuk melalui link login yang diberikan pemilik toko.</p>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-4 mt-10 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <a data-testid="cs-whatsapp-link" href="https://wa.me/6285189784834" target="_blank" rel="noreferrer"
            className="bg-[#25D366] rounded-xl p-3.5 text-white flex items-center gap-2.5 active:scale-[0.98] transition-transform">
            <MessageCircle size={22} className="flex-shrink-0" />
            <span className="text-xs font-bold leading-tight">CS WhatsApp<br /><span className="text-[13px] font-extrabold">0851-8978-4834</span></span>
          </a>
          <a data-testid="tiktok-link" href="https://www.tiktok.com/@tukang_solar?_r=1&_t=ZS-9AmfDgeooab" target="_blank" rel="noreferrer"
            className="bg-black rounded-xl p-3.5 text-white flex items-center gap-2.5 active:scale-[0.98] transition-transform">
            <Music2 size={22} className="flex-shrink-0 text-[#FFCC00]" />
            <span className="text-xs font-bold leading-tight">TikTok Official<br /><span className="text-[13px] font-extrabold text-[#FFCC00]">@tukang_solar</span></span>
          </a>
        </div>
        <div data-testid="refund-terms" className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5">
          <p className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-600" /> Syarat & Ketentuan Pengembalian Dana</p>
          <p className="text-[11px] text-slate-600 leading-relaxed">
            Kami menerima pengajuan pengembalian dana <b>hanya jika disertai bukti unboxing, bukti orderan, dan bukti transfer yang lengkap</b>. Tanpa bukti lengkap, pengajuan tidak dapat diproses. Ada yang ingin ditanyakan? Hubungi CS kami via WhatsApp <a href="https://wa.me/6285189784834" target="_blank" rel="noreferrer" className="font-bold text-emerald-700 underline">0851-8978-4834</a>.
          </p>
        </div>
        <p className="text-center text-[10px] text-slate-400 pb-2">Sistem Orderan Langsung · tukangsolar.com</p>
      </footer>

      {tab === "shop" && step === "shop" && cartItems.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-20">
          <div className="max-w-3xl mx-auto">
            <button data-testid="cart-bar" onClick={() => setStep("checkout")} className="w-full h-12 bg-black hover:bg-slate-900 text-white font-bold rounded-xl flex items-center justify-between px-5 active:scale-[0.98] transition-transform">
              <span className="flex items-center gap-2"><ShoppingBag size={17} /> {cartItems.reduce((a, p) => a + p.qty, 0)} item</span>
              <span className="text-[#FFCC00]">{fmt(total)} → Checkout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
